import React, { FC, ReactElement, useEffect } from 'react'
import { Icons } from '../../Shared/Ant'
import { Button, Checkbox, Pagination, Row, Col, Space } from 'antd'
import { OrderNotification } from './OrderNotification'
import { NotificationsSkeleton } from './Skeleton/NotificationsSkeleton'
import { SortNotification } from './SortNotification'
import SourceFilterDropdown from './SourceFilterDropdown'
import { Snapdocs } from '../../../hooks/api'
import { Requests, ReactQuery } from '../../../api'
import { get } from 'lodash'
import MarkAsReadBulk from './MarkAsReadBulk'
import { pusherProvider } from '../../../utils/pusherProvider'
import { ChannelEvents } from '../../../models/event/channelEvents'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { NotificationsSearchParams } from '../types'
import { NotificationSourceFilter, NotificationSourceFilterValues } from './types'

export const OrderNotifications: FC<{
  isRootCompany: boolean
  searchParams: NotificationsSearchParams
  changeSearchParams: (params: NotificationsSearchParams) => void
}> = ({ isRootCompany, searchParams, changeSearchParams }): ReactElement => {
  const localstorage = window.localStorage
  const owner = searchParams.owner || 'all'
  const page = searchParams.page || 1
  const status = searchParams.status || localstorage.getItem('status') || 'unread'
  const sort = searchParams.sort || localstorage.getItem('sort') || 'received_at_desc'
  const source: NotificationSourceFilter =
    (searchParams.source as NotificationSourceFilter) ||
    (localstorage.getItem('source') as NotificationSourceFilter) ||
    NotificationSourceFilterValues.ALL
  const { path: newOrderPath } = Requests.order({ id: 'new' })
  const queryClient = useQueryClient()

  const notificationsRequest = Requests.notifications({ owner, status, sort, page, source })
  const { data: notificationsData, isLoading: areNotificationsLoading } = useQuery({
    queryKey: [notificationsRequest.type, { owner, status, sort, page, source }],
    queryFn: () => ReactQuery.fetchResources(notificationsRequest)
  })

  // Single request for all counts
  const countsRequest = Requests.unreadNotificationsCount({ owner })
  const { data: countsData } = useQuery({
    queryKey: [countsRequest.type, { owner }],
    queryFn: () => ReactQuery.fetchResources(countsRequest)
  })

  // Extract counts from single response (keys are converted from snake_case to camelCase)
  const allCount = get(countsData, 'resources.unreadCount', 0)
  const autoPinnedCount = get(countsData, 'resources.autoPinner', 0)
  const notAutoPinnedCount = get(countsData, 'resources.notAutoPinner', 0)

  // Determine which count to use based on current source filter
  const currentUnreadCount =
    source === NotificationSourceFilterValues.AUTO_PINNER
      ? autoPinnedCount
      : source === NotificationSourceFilterValues.NOT_AUTO_PINNER
      ? notAutoPinnedCount
      : allCount

  const { response: appConfig } = Snapdocs.useResources(Requests.appConfig())
  const { response: company } = Snapdocs.useResources(Requests.company({ id: appConfig?.companyId }))

  const renderNotifications = get(notificationsData, 'resources', []).map((notification: any) => (
    <OrderNotification key={notification.id} notification={notification} status={status} />
  ))

  const changeStatus = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newStatus = e.target.checked ? 'unread' : 'all'
    localstorage.setItem('status', newStatus)
    changeSearchParams({ ...searchParams, status: newStatus, page: 1 })
  }

  const changeSort = (value: string) => {
    localstorage.setItem('sort', value)
    changeSearchParams({ ...searchParams, sort: value, page: 1 })
  }

  const changeSource = (newSource: NotificationSourceFilter) => {
    localstorage.setItem('source', newSource)
    changeSearchParams({ ...searchParams, source: newSource, page: 1 })
  }

  const changePage = (page: number) => {
    changeSearchParams({ ...searchParams, page })
  }

  const documentTitle = () => document.title.split(') ').pop() || ''

  useEffect(() => {
    document.title = currentUnreadCount > 0 ? `(${currentUnreadCount}) ${documentTitle()}` : documentTitle()
  }, [countsData, source])

  useEffect(() => {
    if (!appConfig) return

    const pusher = pusherProvider(appConfig.pusherAppKey, appConfig.pusherAppCluster)

    const channels = appConfig.companyChannels.map((channelName: string) => pusher.subscribe(channelName))

    for (const channel of channels) {
      channel.bind(ChannelEvents.OrderNotificationAdded, () => {
        queryClient.invalidateQueries({ queryKey: [notificationsRequest.type] })
        queryClient.invalidateQueries({ queryKey: [countsRequest.type] })
      })
    }

    return () => {
      for (const channel of channels) {
        channel.unbind(ChannelEvents.OrderNotificationAdded)
      }
    }
  }, [appConfig, queryClient, notificationsRequest.type, countsRequest.type])

  if (areNotificationsLoading) {
    return <NotificationsSkeleton />
  }

  return (
    <div className="content" style={{ marginRight: 13 }}>
      <div className="order-notifications" data-testid="order-notifications">
        <Row className="header" align="middle">
          <Col xs={24} lg={18}>
            <Space wrap align="center" size={8}>
              <SortNotification selectedValue={sort} handleChange={changeSort} />
              <Checkbox
                onChange={changeStatus}
                checked={status === 'unread'}
                data-test="only-show-unread-notifications"
              >
                Only show unread notifications ({currentUnreadCount})
              </Checkbox>
              {(company?.automatedSweeping ?? false) && (
                <SourceFilterDropdown
                  autoPinnedCount={autoPinnedCount}
                  notAutoPinnedCount={notAutoPinnedCount}
                  selectedSource={source}
                  onSourceChange={changeSource}
                />
              )}
              <MarkAsReadBulk unreadCount={currentUnreadCount} source={source} />
            </Space>
          </Col>
          {!isRootCompany && (
            <Col xs={24} lg={6}>
              <div className="text-right">
                <Button type="primary" href={newOrderPath}>
                  + Create order
                </Button>
              </div>
            </Col>
          )}
        </Row>
        {get(notificationsData, 'resources', []).length > 0 ? (
          renderNotifications
        ) : (
          <Row>
            <Col span="24" className="no-new-notifications text-center">
              <Icons.Bell className="icon" />
              <div className="text">No notifications at this time</div>
            </Col>
          </Row>
        )}
        <Row className="footer">
          <Col span="12" offset="6" className="text-center">
            <Pagination
              showQuickJumper
              showSizeChanger={false}
              hideOnSinglePage={true}
              current={get(notificationsData, 'meta.currentPage')}
              pageSize={get(notificationsData, 'meta.perPage')}
              total={get(notificationsData, 'meta.totalEntries')}
              onChange={changePage}
            />
          </Col>
        </Row>
      </div>
    </div>
  )
}
